import type { TemplateResult } from 'lit'
import type { Page } from '../paging'
import type { Step } from '../state'
import type { DropdownState } from './dropdown'
import type { QuestionHandlers, QuestionState } from './question'
import { css, html, nothing } from 'lit'
import { keyed } from 'lit/directives/keyed.js'
import { iconCaretLeft, iconCaretRight } from './icons'
import { peek } from './peek'
import { questionView } from './question'

// The active question flow body. Pure: the element owns the state and passes a vm +
// handlers. Extracted from survey-element to keep that file lean.
// DropdownState 提供 optionAnswers 與展開中的 dropdown 座標。
export interface ActiveVm extends DropdownState {
  // 已經切好的當前頁（見 paging.ts derivePage）：元件與這裡看到的必然是同一頁，分頁只有 derivePage
  // 一處在切。（pageIdx 仍在 Page 裡，導覽鈕要用。）完整的題目只以 page.sizingSteps 進來，只用於
  // inline 的撐高層，不參與分頁。
  page: Page
  textAnswers: Record<string, string>
  pending: boolean
  errMsg: string
  // 已完成進度 0-100；null = 不顯示進度條（單頁問卷）。mobile 的 4px 細進度條貼在 action
  // bar 底（Figma 313:2901）；desktop 的厚進度條在 frame Hero 底（見 frame.ts）。
  progress: number | null
  // 按下推進鈕時驗證未通過的題目 id。推進鈕本身一律可按 —— 按下去才是使用者得知缺哪一題的方式。
  invalidQIds: string[]
  // 排在作答內容最前面的診斷訊息（preview 專用，見 survey-element.previewNotice）。inline 渲染在
  // live 捲動區的開頭，popup 渲染在 .answer-scroll 之前 —— 兩者都不進撐高層。
  notice?: TemplateResult | typeof nothing
}
// 作答區的 handler 就是題目的 handler 再加上換頁 —— 直接沿用 QuestionHandlers，
// 免得每加一個題型互動都要在這裡補一份同名欄位。
export interface ActiveHandlers extends QuestionHandlers {
  back: () => void
  next: () => void
}

// Nav footer + form hints + page-step separators. (Page count now lives in the frame's
// progress bar; question/confirm body styles live in questionStyles/confirmStyles, composed
// alongside this in the element's shadow root.)
export const activeStyles = css`
  /* preview-notice 是 preview 專用的診斷訊息（見 survey-element.previewNotice），視覺與
     form-hint 相同但 class 分開 —— scrollToHint() 靠 .form-hint 選出送出提示，共用會選錯。 */
  .form-hint,
  .preview-notice { margin-top: 8px; font-size: 12px; color: var(--mp-alert); }
  /* 一頁一題：不畫 separator（Figma 題目與 Action Bar 之間無線；舊 border-bottom 因後面
     跟著 div.nav、:last-of-type 永遠不中，成了題目下方多出來的一條線）。 */
  .page-step { padding: 16px 0; }
  .page-step:first-of-type { padding-top: 0; }
  .page-step-label { font-size: 12px; color: var(--mp-text-secondary); margin-bottom: 8px; }
  /* 題目列：左固定 Q-num 欄 + 右題目內容（標題列 + 縮排選項），對齊 Figma 280:2377。
     尺寸 mobile 16px/26 欄寬（Figma 313:2894）、desktop 20px/32 欄寬（Figma 166:2423）。 */
  .q-row { display: flex; gap: 4px; align-items: flex-start; }
  .q-num {
    flex: none;
    width: 26px;
    /* Figma Type/Number/*（Open Sans）；stack 與 fallback 見 theme.ts 的 --mp-font-number。 */
    font-family: var(--mp-font-number);
    font-size: 16px;
    font-weight: 700;
    line-height: 1.5;
    color: var(--mp-text-meta);
  }
  /* container-type 讓選項用 container query 判斷自身寬度切換 1/2 欄（嵌入寬度不定，不能用 viewport media）。 */
  .q-body { flex: 1 1 auto; min-width: 0; container-type: inline-size; }
  /* 題目與按鈕列之間的接縫。彈出版型（sidebar / modal / manual）沿用「body 捲動 + sticky bar
     疊在上面」的既有契約，所以這個容器不參與版面；inline 則把它變成真正的捲動區（見檔尾），
     因為那邊的按鈕列與選項是上下並排的同一層。display:contents 讓它在前者完全消失，
     不會多出一層 flex item 打亂 .frame-body 既有的排版。 */
  .answer-scroll { display: contents; }
  /* Sticky Action Bar（Figma 313:2899）：釘在捲動容器底、漸層底、無邊線。
     負 margin 用 frame 供應的 padding 變數貼齊卡片邊 —— 三個邊都要貼到，bar 沒蓋到的地方
     捲上來的選項就會從那裡露出來。
     背景照設計稿是一條**由上而下的漸層**（bg/overlay-soft → bg/overlay-weak），不是實色：
     捲到底下的選項在上緣淡出、往下完全被蓋掉。起點是底色的 20% alpha 而非 transparent
     —— 後者讓上緣完全不遮，選項整段透出來、甚至穿過描邊「上一題」鈕的透明面（LAB-2034 圖一）。
     ⚠️ 40% 這個停點是刻意跟收合卡的 CTA（166:2098）對齊，而**不是** 313:2899 的字面定義
     （那條漸層走完整個 bar 高度）。照後者實作量出來的結果是：bar 上沒有任何一段到達實色，
     描邊鈕背後只有 31%（上緣）到 77%（下緣）不透明，選項照樣穿過鈕面 —— 正是 LAB-2034
     圖一那張截圖。取 40% 之後按鈕從上緣往下 20px 起就是全實色。 */
  /* sticky 的 bottom 量的是捲動容器扣掉 padding 之後的邊（.frame-body 的 padding-bottom），
     bottom: 0 會讓 bar 停在卡片底上方一個 padding 高，捲上來的選項從那條縫露出來。bottom 取
     負的 padding 才貼齊卡片底；內容放得下時 bar 本來就靠 margin 落在卡片底，sticky 不再把它往上推。 */
  .frame--answering .nav {
    position: sticky;
    bottom: calc(var(--frame-pad-bottom) * -1);
    display: flex;
    gap: 16px;
    /* auto top margin consumes spare body height for short questions; once content overflows,
       it collapses to zero and sticky keeps the bar at the scroll viewport edge. */
    margin: auto calc(var(--frame-pad-x) * -1) calc(var(--frame-pad-bottom) * -1);
    padding: 12px 12px 20px;
    background: linear-gradient(var(--mp-overlay-soft), var(--mp-bg-base) 40%);
  }
  /* mobile 4px 細進度條：貼 action bar 底緣（Figma 313:2901）；desktop 由 Hero 厚條取代。 */
  .nav-progress {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    overflow: hidden;
    background: var(--mp-scrim-soft);
  }
  .nav-progress-fill {
    position: absolute;
    inset: 0;
    width: 100%;
    transform-origin: left;
    background: var(--mp-brand-weak);
    transition: transform 0.3s;
  }
  @container frame (min-width: 640px) {
    .q-num {
      width: 32px;
      font-size: 20px;
      font-weight: 600;
    }
    .frame--answering .nav {
      padding: 16px 40px 20px;
    }
    .nav-progress { display: none; }
  }
  .nav-icon { flex: none; width: 20px; height: 20px; }
  /* 按鈕本身的長相在 controls.ts；這裡只決定它在 action bar 裡怎麼排。
     desktop：上一題固定 140、下一題填滿剩餘寬度（Figma 280:2396 的 140 + 564）。 */
  .nav .btn-primary {
    flex: 1;
    min-width: 140px;
  }
  /* Mobile（Figma 313:2899/2980）：兩顆各據一端（上一題 x=12、下一題 x=250，容器 402、
     各 140 寬 → 兩側等距 12），並降到 Size=M（40 高、radius 12）。降級 scope 在 .nav 內 ——
     致謝鈕與收合卡的 CTA 在窄容器仍是 Size=L。 */
  @container frame (max-width: 639.98px) {
    .nav { justify-content: space-between; }
    .nav .btn-primary, .nav .btn-secondary {
      flex: 0 0 auto;
      min-width: 140px;
      height: 40px;
      border-radius: 12px;
      padding: 8px 20px;
    }
    /* 第一頁沒有「上一題」時 primary 是首個子節點 —— 靠右，不被 space-between 拉到左邊。 */
    .nav > .btn-primary:first-child { margin-left: auto; }
  }
  /* ===== Inline 版型覆寫（Figma 298:2435/2436）=====
     按鈕左右分佈（上一題靠左、下一題靠右）；粗 progress 在 header 內（見 frame.ts），
     4px 細條收掉。放檔尾後到先贏。
     ⚠️ inline 的按鈕列**不是疊在選項上的 sticky bar**，而是與選項上下並排的同一層 ——
     Figma 298:2343（mobile）是「Q1 高 207 + 間距 12 + 按鈕列 40 = 容器 259」，322:21938
     （desktop）是「內容 238 + 12 + 48 = 298」，容器高度剛好等於兩者相加。所以選項超出時要在
     **內容區自己的捲動範圍內**收掉，不會走到按鈕那一格；也因此這裡沒有遮蔽用的漸層底
     （設計稿的 Button Group 沒有背景）。捲動下放到 .answer-scroll，見下面那條。 */
  .frame--inline .nav {
    position: static;
    justify-content: space-between;
    margin: 12px 0 0;
    padding: 0;
    background: none;
  }
  /* 捲動改由內容區擁有，body 只負責排版 —— 兩條要同時成立，否則 body 仍在捲、
     .answer-scroll 永遠撐不出捲軸。 */
  .frame--inline.frame--answering .frame-body {
    overflow: hidden;
  }
  /* inline 的面板高度 = 整份問卷最高那題的自然高（有上限，見 geometry.ts 的 capped），換題不變。
     做法是兩層疊在同一個 .answer-area 裡：
       .answer-scroll（live）絕對定位鋪滿，只放當前題，自己捲；它不參與 .answer-area 的內容高。
       .answer-sizer（ghost）把全部題目疊在同一格，撐出最高那題的高度，但不可見、不可互動。
     所以 .answer-area 的內容高只來自 ghost；碰到上限時 flex 把它壓小，live 自己捲，scrollHeight
     只來自當前題，短題不會捲進空白。 */
  .frame--inline .answer-area {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }
  /* display:block 不能省：上面基礎規則是 display:contents，絕對定位對它無效。
     當前題放不下時，peek.ts 在 .frame 寫 --peek-inset、面板上限扣掉它（geometry.ts），作答區連同
     面板一起變矮，讓最後一列可見選項的文字落在淡出帶中段。淡出帶的高 --peek-fade 由 .frame 供應
     （frame.ts），下面的 mask 與 peek.ts 的切線計算都讀它。 */
  .frame--inline .answer-scroll {
    position: absolute;
    inset: 0;
    display: block;
    overflow-y: auto;
  }
  /* 還有沒捲到的內容時（data-fade，peek.ts 掛）下緣 --peek-fade 淡出（Figma 298:2436）。mask
     畫在捲動容器的框上，不跟著內容捲。Chrome 120 以前只認 -webkit- 前綴，兩個都寫。
     scroll-padding-bottom 同寬：鍵盤聚焦捲動時選項停在淡出帶之上，不會被遮掉一半。 */
  .frame--inline .answer-scroll[data-fade] {
    -webkit-mask-image: linear-gradient(#000 calc(100% - var(--peek-fade)), transparent);
    mask-image: linear-gradient(#000 calc(100% - var(--peek-fade)), transparent);
    scroll-padding-bottom: var(--peek-fade);
  }
  /* 每題各包一層 wrapper 疊進同一格（grid-area 1/1）；欄寬 minmax(0, 1fr) 讓長字串不會把軌道撐得
     比 live 寬，否則換行行數與 live 不同、量出來的高度就對不上。 */
  .answer-sizer {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    visibility: hidden;
  }
  .answer-sizer > * {
    grid-area: 1 / 1;
    min-width: 0;
  }
  .frame--inline .nav-progress { display: none; }
  /* 單獨一顆（第一題無上一題）也靠右。 */
  .frame--inline .nav .btn-primary {
    flex: 0 0 auto;
    margin-left: auto;
  }
  /* inline desktop 兩顆同寬 200（Figma 503:23810：x=0 與 x=532，容器 732）。
     同樣 scope 在 .nav —— 這一版型的致謝鈕維持設計稿的 140（322:5348）。 */
  @container frame (min-width: 640px) {
    .frame--inline .nav .btn-primary,
    .frame--inline .nav .btn-secondary { min-width: 200px; }
  }
`

// 單一題目的區塊（題號 + 題目內容）。live 與撐高層走同一份，高度才會對得上。
// data-invalid 是 revealFirstInvalid（reveal.ts）找第一個未通過題目的錨點。
function stepView(step: Step, index: number, state: QuestionState, handlers: QuestionHandlers): TemplateResult {
  return html`
    <div class="page-step" ?data-invalid=${(state.invalidQIds ?? []).includes(String(step.q.id))}>
      <div class="q-row">
        <span class="q-num">Q${index + 1}</span>
        <div class="q-body">${questionView(step, state, handlers)}</div>
      </div>
    </div>
  `
}

// 撐高層的固定狀態：答案空、沒有錯誤、dropdown 關、handler 全是空操作。這就是「作答、驗證錯誤、
// 選「其他」長出輸入框、換題都不會改面板高」的來源 —— 這些狀態在撐高層上根本不存在。
// sizing 讓 ghost 不輸出會與 live 撞到的 id／aria 參照／dropdown 錨點（見 question.ts）。
const SIZING_STATE: QuestionState = { optionAnswers: {}, textAnswers: {}, invalidQIds: [], sizing: true }
const SIZING_HANDLERS: QuestionHandlers = {
  toggleOption() {},
  setText() {},
  openDropdown() {},
  closeDropdown() {},
  setDropdownActive() {},
}

export function activeView(vm: ActiveVm, handlers: ActiveHandlers): TemplateResult {
  const { steps: pageSteps, startIndex: start, pageIdx, isLast, multi, sizingSteps } = vm.page
  const notice = vm.notice ?? nothing
  if (pageSteps.length === 0)
    return html`${notice}<p class="muted">目前沒有可作答的題目。</p>`
  // Q-number 全域編號（跨整份問卷，與結果頁一致）；同頁多題以 .page-step 間距分隔。
  const state: QuestionState = {
    optionAnswers: vm.optionAnswers,
    textAnswers: vm.textAnswers,
    invalidQIds: vm.invalidQIds,
    openDropdownQId: vm.openDropdownQId,
    dropdownActiveIndex: vm.dropdownActiveIndex,
  }
  const steps = pageSteps.map((step, i) => stepView(step, start + i, state, handlers))
  const hint = vm.errMsg ? html`<p class="form-hint">${vm.errMsg}</p>` : nothing
  // live（.answer-scroll）必須排在撐高層前面：aria-labelledby 與 querySelector 依樹序先命中 live。
  // inert + aria-hidden：撐高層不可聚焦、不被讀出；visibility:hidden 另外讓它不可見、不收點擊。
  // live 以頁碼 keyed：每頁一個捲動區節點，換題從頂端開始；同頁作答重繪沿用同一個節點，捲動位置不動。
  const answers = sizingSteps === null
    ? html`${notice}<div class="answer-scroll">${steps}${hint}</div>`
    : html`
        <div class="answer-area">
          ${keyed(pageIdx, html`<div class="answer-scroll" ${peek()}>${notice}${steps}${hint}</div>`)}
          <div class="answer-sizer" inert aria-hidden="true">
            ${sizingSteps.map((step, i) => html`<div>${stepView(step, i, SIZING_STATE, SIZING_HANDLERS)}</div>`)}
          </div>
        </div>
      `
  return html`
    ${answers}
    <div class="nav">
      ${pageIdx > 0
        ? html`<button type="button" class="btn-secondary" @click=${handlers.back}>${iconCaretLeft()}${multi ? '上一頁' : '上一題'}</button>`
        : nothing}
      <button type="button" class="btn-primary" ?disabled=${vm.pending} @click=${handlers.next}>
        ${vm.pending ? '送出中…' : isLast ? '完成' : html`${multi ? '下一頁' : '下一題'}${iconCaretRight()}`}
      </button>
      ${vm.progress === null
        ? nothing
        : html`<div class="nav-progress">
            <div class="nav-progress-fill" style="transform:scaleX(${Math.max(0, Math.min(100, vm.progress)) / 100})"></div>
          </div>`}
    </div>
  `
}
